import { auth, db, firebaseConfig } from '../firebase';
import { initializeApp, getApps, deleteApp } from 'firebase/app';
import {
  getAuth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
} from 'firebase/auth';
import {
  collection,
  getDocs,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
} from 'firebase/firestore';

const USER_COLLECTION = 'E2B_USERS';

// --- Firebase 인증 에러코드를 사용자 친화적 한글 메시지로 변환 ---
const translateAuthError = (e) => {
  const code = e && e.code ? e.code : '';
  switch (code) {
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
      return '이메일 또는 비밀번호가 일치하지 않습니다.';
    case 'auth/invalid-email':
      return '올바른 이메일 형식이 아닙니다.';
    case 'auth/user-disabled':
      return '비활성화된 계정입니다. 관리자에게 문의하세요.';
    case 'auth/too-many-requests':
      return '로그인 시도가 너무 많습니다. 잠시 후 다시 시도해주세요.';
    case 'auth/email-already-in-use':
      return '이미 가입(신청)된 이메일입니다.';
    case 'auth/weak-password':
      return '비밀번호는 6자리 이상이어야 합니다.';
    case 'auth/network-request-failed':
      return '네트워크 연결을 확인해주세요.';
    default:
      return '처리 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.';
  }
};

// --- 관리자가 신규 계정을 만들 때, 본인 로그인 세션이 풀리지 않도록 쓰는 '보조 앱' ---
// (클라이언트에서 createUserWithEmailAndPassword를 호출하면 현재 로그인 계정이 새 계정으로 바뀌는
//  Firebase 특성 때문에, 별도의 임시 앱 인스턴스에서 계정만 생성하고 바로 정리합니다.)
const SECONDARY_APP_NAME = 'E2B_SECONDARY';
const getSecondaryApp = () => {
  const existing = getApps().find((a) => a.name === SECONDARY_APP_NAME);
  return existing || initializeApp(firebaseConfig, SECONDARY_APP_NAME);
};

export const userService = {
  // --- [1. 로그인] Firebase 정식 인증 ---
  login: async (email, password) => {
    try {
      const cred = await signInWithEmailAndPassword(auth, email.trim(), password);
      const uid = cred.user.uid;

      // 로그인 계정에 연결된 프로필(권한/승인상태) 문서 조회
      const snap = await getDoc(doc(db, USER_COLLECTION, uid));
      if (!snap.exists()) {
        await signOut(auth);
        return { success: false, message: '계정 정보를 찾을 수 없습니다. 관리자에게 문의하세요.' };
      }

      const profile = { id: uid, ...snap.data() };

      if (profile.status !== 'APPROVED') {
        await signOut(auth);
        return { success: false, message: '관리자의 승인이 대기 중인 계정입니다.' };
      }

      return { success: true, user: profile };
    } catch (e) {
      console.error('로그인 에러:', e);
      return { success: false, message: translateAuthError(e) };
    }
  },

  // --- [2. 가입 신청] 정식 인증 계정 생성 + PENDING 프로필 ---
  requestSignup: async (signupForm) => {
    try {
      const cred = await createUserWithEmailAndPassword(
        auth,
        signupForm.email.trim(),
        signupForm.password
      );
      const uid = cred.user.uid;

      // 프로필 문서 저장 (비밀번호는 저장하지 않음! 인증은 Firebase가 담당)
      await setDoc(doc(db, USER_COLLECTION, uid), {
        name: signupForm.name || '',
        email: signupForm.email.trim(),
        role: signupForm.role === 'RECALL' ? 'RECALL' : 'STAFF',
        status: 'PENDING',
        createdAt: serverTimestamp(),
      });

      // 승인 전에는 로그인 상태를 유지하지 않음
      await signOut(auth);
      return { success: true };
    } catch (e) {
      console.error('가입 신청 에러:', e);
      return { success: false, message: translateAuthError(e) };
    }
  },

  // --- [3. 관리자 전용: 사용자 직접 등록] 보조 앱으로 계정 생성 후 프로필 저장 ---
  addDirectUser: async (userData) => {
    const secondaryApp = getSecondaryApp();
    const secondaryAuth = getAuth(secondaryApp);
    try {
      const cred = await createUserWithEmailAndPassword(
        secondaryAuth,
        userData.email.trim(),
        userData.password
      );
      const uid = cred.user.uid;

      // 프로필 문서는 '현재 로그인한 관리자' 권한으로 저장됩니다 (규칙상 관리자만 가능)
      await setDoc(doc(db, USER_COLLECTION, uid), {
        name: userData.name || '',
        email: userData.email.trim(),
        role: userData.role || 'STAFF',
        status: userData.status || 'APPROVED',
        createdAt: serverTimestamp(),
      });

      // 보조 앱 세션 정리 (관리자 본인 세션에는 영향 없음)
      await signOut(secondaryAuth);
      return { success: true };
    } catch (e) {
      console.error('사용자 등록 에러:', e);
      return { success: false, message: translateAuthError(e) };
    } finally {
      // 임시 앱 인스턴스 제거 (메모리 정리)
      try { await deleteApp(secondaryApp); } catch (_) { /* 무시 */ }
    }
  },

  // --- [4. 사용자 목록 가져오기] (관리자 화면 전용) ---
  getUsers: async () => {
    try {
      const querySnapshot = await getDocs(collection(db, USER_COLLECTION));
      return querySnapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
    } catch (e) {
      console.error('사용자 목록 로드 실패:', e);
      return [];
    }
  },

  // --- [5. 사용자 승인] ---
  approveUser: async (userId) => {
    try {
      await updateDoc(doc(db, USER_COLLECTION, userId), {
        status: 'APPROVED',
        updatedAt: serverTimestamp(),
      });
      return { success: true };
    } catch (e) {
      console.error('승인 에러:', e);
      return { success: false };
    }
  },

  // --- [6. 사용자 정보 수정] 비밀번호는 Firestore에 저장하지 않음 ---
  updateUser: async (userId, updateData) => {
    try {
      // id, password, email은 프로필 수정 대상에서 제외
      // (password/email 변경은 Firebase 인증 쪽 처리가 필요 → 비밀번호는 재설정 메일 사용)
      const { id, password, email, ...data } = updateData;
      await updateDoc(doc(db, USER_COLLECTION, userId), {
        ...data,
        updatedAt: serverTimestamp(),
      });
      return { success: true };
    } catch (e) {
      console.error('사용자 수정 에러:', e);
      return { success: false };
    }
  },

  // --- [7. 사용자 삭제] 프로필 문서 삭제 (접근 차단) ---
  deleteUser: async (userId) => {
    try {
      // 프로필 문서를 지우면 로그인 시 거부되어 접근이 차단됩니다.
      // (Firebase 인증 계정 자체의 완전 삭제는 Admin SDK가 필요하므로, 필요 시 콘솔에서 삭제)
      await deleteDoc(doc(db, USER_COLLECTION, userId));
      return { success: true };
    } catch (e) {
      console.error('사용자 삭제 에러:', e);
      return { success: false };
    }
  },

  // --- [8. 비밀번호 재설정 메일 발송] ---
  sendPasswordReset: async (email) => {
    try {
      await sendPasswordResetEmail(auth, email.trim());
      return { success: true };
    } catch (e) {
      console.error('비밀번호 재설정 메일 에러:', e);
      return { success: false, message: translateAuthError(e) };
    }
  },

  // --- [8-1. 로그인한 본인이 직접 비밀번호 변경] ---
  changePassword: async (currentPassword, newPassword) => {
    const currentUser = auth.currentUser;
    if (!currentUser || !currentUser.email) {
      return { success: false, message: '로그인 정보가 없습니다. 다시 로그인해주세요.' };
    }
    try {
      // (1) 현재 비밀번호로 재인증하여 본인 확인
      const cred = EmailAuthProvider.credential(currentUser.email, currentPassword);
      await reauthenticateWithCredential(currentUser, cred);
      // (2) 새 비밀번호로 변경
      await updatePassword(currentUser, newPassword);
      return { success: true };
    } catch (e) {
      console.error('비밀번호 변경 에러:', e);
      if (e.code === 'auth/invalid-credential' || e.code === 'auth/wrong-password') {
        return { success: false, message: '현재 비밀번호가 올바르지 않습니다.' };
      }
      if (e.code === 'auth/weak-password') {
        return { success: false, message: '새 비밀번호는 6자리 이상이어야 합니다.' };
      }
      if (e.code === 'auth/requires-recent-login') {
        return { success: false, message: '보안을 위해 로그아웃 후 다시 로그인한 다음 시도해주세요.' };
      }
      return { success: false, message: translateAuthError(e) };
    }
  },

  // --- [9. App.js용] 현재 로그인 계정의 프로필 조회 ---
  getProfile: async (firebaseUser) => {
    if (!firebaseUser) return null;
    try {
      const snap = await getDoc(doc(db, USER_COLLECTION, firebaseUser.uid));
      if (!snap.exists()) return null;
      return { id: firebaseUser.uid, ...snap.data() };
    } catch (e) {
      console.error('프로필 조회 실패:', e);
      return null;
    }
  },

  // --- [10. App.js용] 로그인 상태 변화 구독 / 로그아웃 ---
  subscribeAuth: (callback) => onAuthStateChanged(auth, callback),
  logout: () => signOut(auth),
};
